import {
  Account,
  TokenAccountNotFoundError,
  TokenInvalidAccountOwnerError,
  createAssociatedTokenAccountInstruction,
  createMintToInstruction,
  getAccount,
  getAssociatedTokenAddress,
} from "@solana/spl-token";
import { SendTransactionOptions } from "@solana/wallet-adapter-base";
import {
  Connection,
  Keypair,
  PublicKey,
  Transaction,
  TransactionSignature,
  sendAndConfirmTransaction,
} from "@solana/web3.js";
import BigNumber from "bignumber.js";

export type Sender = (
  transaction: Transaction,
  connection: Connection,
  options?: SendTransactionOptions
) => Promise<TransactionSignature>;

export const requestSolanaTestToken = async (
  connection: Connection,
  amount: number,
  sendTransactionFromAdapter: Sender
): Promise<string> => {
  // INFO: ONLY TEST TOKENS - DO NOT USE IN PRODUCTION ;)
  // Output from deploy.ts | see deploy script of OutbidContracts_Solana https://github.com/axvdex/OutbidContracts_Solana/tree/main/tests/ ~~~~~~
  const mintAuthority = Keypair.fromSecretKey(
    Uint8Array.from([
      159, 209, 68, 19, 175, 152, 50, 225, 70, 72, 136, 61, 254, 146, 207, 162, 145, 163, 201, 68, 145, 204, 93, 122,
      215, 23, 56, 152, 48, 83, 33, 15, 172, 35, 172, 144, 235, 73, 224, 162, 105, 102, 250, 0, 206, 162, 192, 109, 5,
      114, 221, 98, 246, 67, 148, 196, 11, 206, 220, 181, 2, 249, 74, 229,
    ])
  );
  const mint = new PublicKey("Bs2c8Rd1pAxjfLWaBe5L727i5oEuNqiVngEYSDpqNrAo"); // This is the bid asset that need to be replaqced in DummySolanaOutbidData.ts
  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  console.log("Mint Authority", mintAuthority.publicKey.toBase58());
  const res = await getOrCreateAssociatedTokenAccount(
    connection,
    mint,
    window.solanaClient.publicKey,
    sendTransactionFromAdapter
  );
  if (res.ok === false) {
    throw new Error(res.message);
  }
  const userTokenAccount = res.value;

  let accountInfo = await getAccount(connection, userTokenAccount.address);
  console.log("Before balance", accountInfo.amount.toString(), "tokens");
  const signarture = await mintTo(connection, mint, userTokenAccount.address, mintAuthority, amount * Math.pow(10, 9));
  // Wait for the transaction to be confirmed
  await connection.confirmTransaction(signarture, "singleGossip");

  // Display the number of tokens minted
  accountInfo = await getAccount(connection, userTokenAccount.address);
  // Get token decimals
  const mintInfo = await connection.getParsedAccountInfo(mint);
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const balance = BigNumber(accountInfo.amount).div(Math.pow(10, mintInfo.value.data.parsed.info.decimals));
  return balance.toString();
};

async function mintTo(
  connection: Connection,
  mint: PublicKey,
  destination: PublicKey,
  authority: Keypair,
  amount: number | bigint
): Promise<TransactionSignature> {
  const transaction = new Transaction().add(
    createMintToInstruction(mint, destination, authority.publicKey, amount, [authority])
  );
  const { blockhash } = await connection.getLatestBlockhash();
  transaction.recentBlockhash = blockhash;
  return await sendAndConfirmTransaction(connection, transaction, [authority]);
}

type Result<T> =
  | {
      ok: true;
      value: T;
    }
  | {
      ok: false;
      message: string;
    };

export async function getOrCreateAssociatedTokenAccount(
  connection: Connection,
  mint: PublicKey,
  owner: PublicKey,
  sendTransactionFromAdapter: Sender
): Promise<Result<Account>> {
  const associatedToken = await getAssociatedTokenAddress(mint, owner);
  let account;
  try {
    account = await getAccount(connection, associatedToken);
  } catch (error: unknown) {
    if (error instanceof TokenAccountNotFoundError || error instanceof TokenInvalidAccountOwnerError) {
      try {
        const confirmed = confirm("You don't have a account for this token yet. Do you want to create one?");
        if (!confirmed) {
          return { ok: false, message: "User cancelled the operation" };
        }
        // Check if the owner has enought SOL to pay for the creation
        const solBalance = await connection.getBalance(window.solanaClient.publicKey);
        console.log("solBalance", solBalance);
        if (BigNumber(solBalance).eq(0)) {
          return { ok: false, message: "Not enough SOL to pay the transaction fee" };
        }

        const transaction = new Transaction().add(
          createAssociatedTokenAccountInstruction(window.solanaClient.publicKey, associatedToken, owner, mint)
        );

        console.log("Adapter: ", window.solanaClient);
        const signature = await sendTransactionFromAdapter(transaction, connection);
        console.log("Signature: ", signature);
        await connection.confirmTransaction(signature, "singleGossip");

        // sendWithPhantomAndDebugTransaction(transaction, connection);
      } catch (error: unknown) {
        console.error("Failed to create associated token account", error);
        /* Empty */
      }
      account = await getAccount(connection, associatedToken);
    } else {
      console.error("Unexpected Error while creating user Token Account", error);
      return { ok: false, message: "Unexpected Error while creating user Token Account" };
    }
  }
  return { ok: true, value: account };
}
